import React, { useRef, useState, useEffect } from "react";

/**
 * --------------
 * Day / Month / Year text boxes (DD MM YYYY).
 * • Auto-advances focus when max length is reached.
 * • Pads a single-digit day/month on blur, so "14/5/2003" becomes "14/05/2003"
 *   instead of failing the two-digit check and reading as an incomplete date.
 * • Calls `onChange(fullIsoDate)` whenever *any* part changes
 *   – returns an ISO-compatible string **yyyy-mm-dd** or `""` if incomplete.
 *
 * Props
 * -----
 * • value      – current combined value (“yyyy-mm-dd” or “”) from parent form
 * • onChange   – (e) => void  ← identical API to normal <input/> handler
 * • id         – field id (used for name/ids of the three inputs)
 * • required   – mark all three boxes as required
 * • className  – extra Tailwind / CSS classes for the wrapper
 */
export default function DatePartsInput({
  value = "",
  onChange,
  id,
  required,
  className = "",
}) {
  const [parts, setParts] = useState({ dd: "", mm: "", yyyy: "" });

  const dRef = useRef(null);
  const mRef = useRef(null);
  const yRef = useRef(null);

  /**
   * Mirrors `parts` synchronously. Auto-advancing focus fires the previous
   * box's blur *during* the change handler, before React has re-rendered, so a
   * blur handler reading `parts` would see the value from before the keystroke.
   */
  const partsRef = useRef(parts);

  /**
   * The parent `value` these boxes already represent — either one we emitted or
   * one we last synced in. Any half-typed date emits "", so without this the
   * echo of our own emit looks like the parent clearing the field: the first
   * digit typed into an empty DOB (parent `null` → "") got wiped, and editing
   * one box of a complete date ("yyyy-mm-dd" → "") wiped the other two.
   */
  const syncedValueRef = useRef(null);

  /* mimic native <input/> event shape so parent handler stays the same */
  const emitParts = (nextParts) => {
    const { dd, mm, yyyy } = nextParts;
    const full =
      dd.length === 2 && mm.length === 2 && yyyy.length === 4
        ? `${yyyy}-${mm}-${dd}`
        : "";

    syncedValueRef.current = full;
    onChange({
      target: { name: id, value: full },
    });
  };

  const commitParts = (nextParts) => {
    partsRef.current = nextParts;
    setParts(nextParts);
    emitParts(nextParts);
  };

  /* keep internal state in sync when parent value changes */
  useEffect(() => {
    // Absent answers reach us as null/undefined as well as "" — all mean empty,
    // so a switch between them is not an incoming value to sync.
    const incoming = typeof value === "string" ? value : "";
    if (incoming === syncedValueRef.current) return;
    syncedValueRef.current = incoming;

    if (!incoming) {
      partsRef.current = { dd: "", mm: "", yyyy: "" };
      setParts(partsRef.current);
      return;
    }
    const [yyyy, mm, dd] = incoming.split("-");
    partsRef.current = { dd: dd ?? "", mm: mm ?? "", yyyy: yyyy ?? "" };
    setParts(partsRef.current);
  }, [value]);

  const handlePartChange = (key, maxLen, nextRef) => (e) => {
    const raw = e.target.value.replace(/\D/g, ""); // numbers only
    const trimmed = raw.slice(0, maxLen);

    // Commit before moving focus, so the blur this triggers on the box being
    // left reads the keystroke that caused it rather than the previous value.
    commitParts({ ...partsRef.current, [key]: trimmed });

    /* auto-advance focus */
    if (trimmed.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  /**
   * Pads a lone digit once the user leaves the box — "5" → "05", so "14/5/2003"
   * is accepted as 14/05/2003. Padding while they type would round-trip through
   * the parent `value` and rewrite half-typed entries, so it waits for blur.
   * "0" is left alone; it is not a day or month the user could have meant, and
   * validation should reject it as typed.
   */
  const handlePartBlur = (key) => () => {
    const current = partsRef.current[key];
    if (current.length !== 1 || current === "0") return;

    commitParts({ ...partsRef.current, [key]: current.padStart(2, "0") });
  };

  /**
   * Opts the three boxes out of credential autofill.
   *
   * The semantic `bday-day`/`bday-month`/`bday-year` tokens are deliberately
   * dropped: iOS Safari does not reliably classify them, and an autocomplete
   * token it does not act on falls back to generic autofill — which is why it
   * offers saved passwords on each box. `off` plus an explicit non-credential
   * `name` matches how StudyAccessPage opts its ID fields out. The `data-*`
   * flags are the equivalent opt-out for 1Password/LastPass/Dashlane.
   */
  const autofillOptOutProps = (part) => ({
    name: `${id}__${part}`,
    autoComplete: "off",
    spellCheck: "false",
    "data-1p-ignore": "true",
    "data-lpignore": "true",
    "data-form-type": "other",
  });

  const handlePartKeyDown = (key, previousRef) => (event) => {
    if (event.key !== "Backspace" || parts[key] !== "" || !previousRef?.current) {
      return;
    }

    previousRef.current.focus();
  };

  return (
    <div
      data-testid={`${id}__date-parts-grid`}
      className={`grid w-full min-w-0 grid-cols-4 gap-3 ${className}`.trim()}
    >
      <label
        data-testid={`${id}__day-field`}
        className="col-span-1 flex min-w-0 flex-col gap-2"
      >
        <span className="text-base font-semibold text-typography">Day</span>
        <input
          ref={dRef}
          id={`${id}__day`}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          {...autofillOptOutProps("day")}
          aria-label="Day"
          placeholder="DD"
          value={parts.dd}
          onChange={handlePartChange("dd", 2, mRef)}
          onBlur={handlePartBlur("dd")}
          onKeyDown={handlePartKeyDown("dd")}
          maxLength={2}
          required={required}
          className="custom-input w-full text-center"
        />
      </label>
      <label
        data-testid={`${id}__month-field`}
        className="col-span-1 flex min-w-0 flex-col gap-2"
      >
        <span className="text-base font-semibold text-typography">Month</span>
        <input
          ref={mRef}
          id={`${id}__month`}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          {...autofillOptOutProps("month")}
          aria-label="Month"
          placeholder="MM"
          value={parts.mm}
          onChange={handlePartChange("mm", 2, yRef)}
          onBlur={handlePartBlur("mm")}
          onKeyDown={handlePartKeyDown("mm", dRef)}
          maxLength={2}
          required={required}
          className="custom-input w-full text-center"
        />
      </label>
      <label
        data-testid={`${id}__year-field`}
        className="col-span-2 flex min-w-0 flex-col gap-2"
      >
        <span className="text-base font-semibold text-typography">Year</span>
        <input
          ref={yRef}
          id={`${id}__year`}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          {...autofillOptOutProps("year")}
          aria-label="Year"
          placeholder="YYYY"
          value={parts.yyyy}
          onChange={handlePartChange("yyyy", 4)}
          onKeyDown={handlePartKeyDown("yyyy", mRef)}
          maxLength={4}
          required={required}
          className="custom-input w-full text-center"
        />
      </label>
    </div>
  );
}
